import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Chip, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Document, DocumentPDF, DocumentXLS } from 'ui-kit/icons/DocIcons';
import { formatNameTag } from 'utilities/utilities';
import { getFileType } from './fileUtils';
import { NotesType } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      paddingRight: theme.spacing(2),
    },
    conversation: {
      backgroundColor: theme.palette.brand.feather,
      borderRadius: 4,
      color: theme.palette.text.primary,
      fontSize: pxToRem(14),
      padding: theme.spacing(1),
      maxWidth: '75%',
      width: 'max-content',
    },
    icon: {
      alignItems: 'center',
      backgroundColor: theme.palette.primary.light,
      borderRadius: 32,
      color: theme.palette.primary.main,
      display: 'flex',
      flex: '0 0 32px',
      fontWeight: 500,
      height: 32,
      justifyContent: 'center',
      marginTop: 23,
      width: 32,
    },
    system: {
      alignItems: 'flex-end',
    },
    systemUser: {
      backgroundColor: theme.palette.brand.feather50,
    },
    time: {
      marginBottom: theme.spacing(0.5),
    },
    userName: {
      marginRight: theme.spacing(1),
    },
    fileChip: {
      marginTop: theme.spacing(0.5),
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      transition: 'all 0.2s ease',
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        boxShadow: theme.shadows[1],
      },
    },
  }),
);

type NoteAttachmentItemProps = {
  conversation: NotesType;
  profileUserId: number | undefined;
  onFileClick?: (uploadFileName: string, fileName: string) => void;
};

export function NoteAttachmentItem({ conversation, profileUserId, onFileClick }: NoteAttachmentItemProps) {
  const classes = useStyles();
  const formattedTime = conversation.createdDate
    ? format(new Date(conversation.createdDate), 'MMM d, yyyy h:mm a').toLocaleString()
    : '';
  const isUser =
    conversation.createdByUserId && profileUserId && conversation.createdByUserId !== profileUserId ? true : false;

  const nameTag = (): string => {
    return formatNameTag(conversation.userName || '');
  };

  function getFileIconByType(fileType: string): JSX.Element {
    if (fileType === 'pdf') {
      return <DocumentPDF />;
    }
    if (fileType === 'excel') {
      return <DocumentXLS />;
    }
    return <Document />;
  }

  const renderFileAttachment = () => {
    if (!conversation.fileNameText || !conversation.uploadedFileNameText) return null;
    const fileType = getFileType(conversation.fileNameText);
    const fileIcon = getFileIconByType(fileType);

    return (
      <div>
        <Chip
          icon={fileIcon}
          label={conversation.fileNameText}
          variant="outlined"
          size="medium"
          clickable
          onClick={() =>
            conversation.uploadedFileNameText &&
            conversation.fileNameText &&
            onFileClick &&
            onFileClick(conversation.uploadedFileNameText, conversation.fileNameText)
          }
          className={classes.fileChip}
        />
      </div>
    );
  };
  return (
    <div className={classes.root}>
      {isUser && <div className={classes.icon}>{nameTag()}</div>}
      <div className={clsx(classes.content, { [classes.system]: !isUser })}>
        <Typography className={classes.time} variant="body2">
          {isUser && <strong className={classes.userName}>{conversation.userName}</strong>}
          <Typography color="textSecondary" component="span" variant="body2">
            {formattedTime}
          </Typography>
        </Typography>

        <div className={clsx(classes.conversation, { [classes.systemUser]: !isUser })}>
          {conversation.notes}
          {renderFileAttachment()}
        </div>
      </div>
    </div>
  );
}
